/* Native gallery: the selected large image is requested only when opened. */
if (!customElements.get('leisure-photo-gallery')) {
  customElements.define('leisure-photo-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.links = [...this.querySelectorAll('[data-gallery-photo]')];
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || !this.links.length) return;
      this.stage = this.querySelector('[data-gallery-stage]');
      this.caption = this.querySelector('[data-gallery-caption]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.status = this.querySelector('[data-gallery-status]');
      this.original = this.querySelector('[data-gallery-original]');
      this.closeButton = this.querySelector('[data-gallery-close]');
      this.index = 0;
      this.shown = Math.min(24, this.links.length);
      this.more = this.querySelector('.llg-more');
      this.moreButton = this.querySelector('[data-gallery-more]');
      if (this.more && this.links.length > 24) {
        this.links.forEach((link, index) => { link.closest('li').hidden = index >= this.shown; });
        this.more.hidden = false;
        this.updateMore();
        this.moreButton.addEventListener('click', () => {
          const firstNew = this.shown;
          this.shown = Math.min(this.shown + 24, this.links.length);
          this.links.slice(firstNew, this.shown).forEach(link => { link.closest('li').hidden = false; });
          this.updateMore();
          this.links[firstNew]?.focus({preventScroll: true});
        }, options);
      }
      this.links.forEach((link, index) => link.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !this.dialog.showModal) return;
        event.preventDefault();
        this.opener = link;
        this.previousOverflow = document.documentElement.style.overflow;
        this.dialog.showModal();
        document.documentElement.style.overflow = 'hidden';
        this.show(index);
        this.closeButton.focus({preventScroll: true});
      }, options));
      this.closeButton.addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('[data-gallery-prev]').addEventListener('click', () => this.show(this.index - 1), options);
      this.querySelector('[data-gallery-next]').addEventListener('click', () => this.show(this.index + 1), options);
      this.dialog.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.stage.replaceChildren();
        this.opener?.focus({preventScroll: true});
      }, options);
      // A horizontal one-finger gesture advances photos; pinch zoom remains native.
      this.stage.addEventListener('touchstart', event => {
        this.touchStart = event.touches.length === 1 ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null;
      }, {...options, passive:true});
      this.stage.addEventListener('touchend', event => {
        if (!this.touchStart || event.touches.length || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touchStart.x;
        const dy = event.changedTouches[0].clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1));
      }, {...options, passive:true});
    }
    updateMore() {
      this.querySelector('[data-gallery-shown]').textContent = `${this.shown} of ${this.links.length} photos shown`;
      const remaining = this.links.length - this.shown;
      this.moreButton.hidden = remaining === 0;
      this.moreButton.textContent = `Show ${Math.min(24, remaining)} more photos`;
    }
    show(index) {
      this.index = (index + this.links.length) % this.links.length;
      const link = this.links[this.index];
      const thumbnail = link.querySelector('img');
      const image = new Image();
      image.alt = thumbnail.alt;
      image.decoding = 'async';
      image.className = 'llg-view-image';
      this.status.textContent = 'Loading photo…';
      this.stage.setAttribute('aria-busy', 'true');
      const finished = message => {
        if (this.stage.firstElementChild !== image) return;
        this.status.textContent = message;
        this.stage.setAttribute('aria-busy', 'false');
      };
      image.addEventListener('load', () => finished(''), {once:true});
      image.addEventListener('error', () => finished('This photo could not load. Try the original photo link below.'), {once:true});
      this.stage.replaceChildren(image);
      this.caption.textContent = thumbnail.alt;
      this.counter.textContent = `${this.index + 1} / ${this.links.length}`;
      this.original.href = link.href;
      image.sizes = '(max-width: 749px) 100vw, 90vw';
      const photoUrl = new URL(link.href);
      const resized = width => { photoUrl.searchParams.set('width', width); return photoUrl.href; };
      image.srcset = [800, 1200, 1600, 2400].map(width => `${resized(width)} ${width}w`).join(', ');
      image.src = resized(1600);
    }
    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow || '';
      }
      this.abort?.abort();
      this.abort = null;
    }
  });
}
